import { useEffect } from 'react';
import { useChatStore } from '../store/useChatStore';
import { useStore } from '../store/useStore';
import ChatSidebar from '../components/chat/ChatSidebar';
import ChatWindow from '../components/chat/ChatWindow';
import MessageInput from '../components/chat/MessageInput';
import ModelSelector from '../components/chat/ModelSelector';
import { MessageSquare, Sparkles, X } from 'lucide-react';

export default function ChatPage() {
    const { user } = useStore();
    const {
        chats,
        currentChat,
        messages,
        isSending,
        selectedModel,
        availableModels,
        error,
        loadUserChats,
        createNewChat,
        selectChat,
        sendMessage,
        setSelectedModel,
        loadModels,
        deleteChat,
        clearError
    } = useChatStore();

    useEffect(() => {
        loadModels();
        // Atualiza a lista de modelos a cada 5 minutos
        const interval = setInterval(() => {
            loadModels();
        }, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [loadModels]);

    useEffect(() => {
        if (user) {
            loadUserChats(user.id);
        }
    }, [user, loadUserChats]);

    const handleNewChat = () => {
        if (user) {
            createNewChat(user.id, selectedModel);
        }
    };

    return (
        <div className="flex h-[calc(100vh-4rem)] bg-navy-50 dark:bg-navy-950 rounded-xl overflow-hidden border border-navy-200 dark:border-navy-800">
            <ChatSidebar
                chats={chats}
                currentChatId={currentChat?.id}
                onSelectChat={selectChat}
                onNewChat={handleNewChat}
                onDeleteChat={deleteChat}
            />

            <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-navy-900">
                <div className="p-4 border-b border-navy-200 dark:border-navy-700 flex justify-between items-center">
                    <div className="flex-1 max-w-xs">
                        <ModelSelector
                            selectedModel={selectedModel}
                            onSelect={setSelectedModel}
                            models={availableModels}
                        />
                    </div>
                    {currentChat && (
                        <div className="text-sm text-navy-500 dark:text-navy-400 bg-navy-100 dark:bg-navy-800 px-3 py-1 rounded-full">
                            Tokens: {Math.round(currentChat.tokenUsage)}
                        </div>
                    )}
                </div>

                {error && (
                    <div className="p-4 bg-error-50 dark:bg-error-900/20 border-l-4 border-error-500 text-error-700 dark:text-error-400 flex justify-between items-center">
                        <span>{error}</span>
                        <button
                            onClick={clearError}
                            className="p-1 hover:bg-error-100 dark:hover:bg-error-900/30 rounded transition-colors duration-200"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                )}

                {currentChat ? (
                    <>
                        <ChatWindow messages={messages} isLoading={isSending} />
                        <MessageInput
                            onSend={sendMessage}
                            disabled={isSending || currentChat.isArchived}
                            placeholder={currentChat.isArchived ? "Conversa arquivada" : "Digite sua mensagem..."}
                        />
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="text-center max-w-md px-6">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-400/10 dark:bg-accent-400/20 rounded-full mb-4">
                                <MessageSquare className="h-8 w-8 text-accent-500 dark:text-accent-400" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2 text-navy-900 dark:text-navy-100">
                                Bem-vindo ao Hub de IAs
                            </h3>
                            <p className="text-navy-500 dark:text-navy-400 mb-6">
                                Selecione um modelo e inicie uma nova conversa para tirar dúvidas jurídicas.
                            </p>
                            <button
                                onClick={handleNewChat}
                                className="inline-flex items-center px-5 py-2.5 btn-accent rounded-lg"
                            >
                                <Sparkles className="h-5 w-5 mr-2" />
                                Iniciar Nova Conversa
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
